import React from 'react';

import {browserHistory, WithRouterProps} from 'react-router';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {RequestBrowserNotificationAlert} from 'app/components/RequestBrowserNotificationAlert';
import {useCurrentUserState} from 'app/current-user/api';
import {
  DashboardFilterbarStructure,
  getFilterLink,
} from 'app/general/dashboards/DashboardFilterbar';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useNotifications, useUpdateNotification} from 'app/notifly/api';
import {gt} from 'app/utils/i18n';

import MarkAllReadButton from './MarkAllReadButton';
import {NotificationItem} from './NotificationItem';

import {useNotificationFilters} from '../hooks';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {
  NotificationsFetchParamsType,
  NotificationsLocationParamsType,
  NotificationType,
} from 'app/notifly/types';

export function NotificationsPage(
  routerProps: WithRouterProps
): JSX.Element | null {
  const [updateNotification] = useUpdateNotification();
  const {url: currentUserURL} = useCurrentUserState().data!;

  const showNotifications = (isAll: boolean) => {
    const query = {
      is_all: isAll,
      page: '1',
    };
    const link = getFilterLink(
      browserHistory,
      browserHistory.getCurrentLocation(),
      query
    );
    routerProps.router.replace(link);
  };

  return (
    <DashboardPage<
      NotificationType,
      NotificationsLocationParamsType,
      NotificationsFetchParamsType
    >
      {...routerProps}
      title={gt('Notifications')}
      renderItem={(object) => (
        <NotificationItem
          key={object.url}
          currentUserURL={currentUserURL}
          object={object}
          markNotificationRead={(notification) =>
            updateNotification({...notification, read_at: 'true'})
          }
        />
      )}
      actionArea={<MarkAllReadButton />}
      useDataHook={useNotifications}
      useFiltersHook={useNotificationFilters}
      filterbar={({viewingAll}) => (
        <DashboardFilterbarStructure>
          <span className="mr-1">{gt('Show')}</span>
          <div className="btn-group btn-group-sm ml-1" role="group">
            <button
              className={`btn btn-light ${viewingAll ? 'active' : ''}`}
              onClick={() => showNotifications(true)}
            >
              {gt('All')}
            </button>
            <button
              className={`btn btn-light ${viewingAll ? '' : 'active'}`}
              onClick={() => showNotifications(false)}
            >
              {gt('Unread')}
            </button>
          </div>
        </DashboardFilterbarStructure>
      )}
      messages={{
        notFound: (props, NoResultsMessage) => (
          <NoResultsMessage icon="fa-bell" head={gt('No new notifications')}>
            {gt(
              'You will be notified of changes your teammates make to your SweetProcess account here.'
            )}
          </NoResultsMessage>
        ),
      }}
      onModalChangeMessage={({refetch}) =>
        (message, {runWhenAny, anyModelMatch}) => {
          runWhenAny(message, [anyModelMatch(['notification'])], refetch);
        }}
      footer={() => <RequestBrowserNotificationAlert />}
    />
  );
}

export const NotificationsPageBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    text={gt('Notifications')}
    icon="fa-bell"
  />
);
